import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { MapPin, Phone, Clock, Star, MessageCircle } from 'lucide-react';

const ServiceAreasDetailed = ({ currentLang, translations }) => {
  const regions = [
    {
      name: { en: "North", he: "צפון", ru: "Север" },
      cities: [
        { name: "Haifa", nameHe: "חיפה", nameRu: "Хайфа" },
        { name: "Nahariya", nameHe: "נהריה", nameRu: "Нагария" },
        { name: "Acre", nameHe: "עכו", nameRu: "Акко" },
        { name: "Tiberias", nameHe: "טבריה", nameRu: "Тверия" },
        { name: "Nazareth", nameHe: "נצרת", nameRu: "Назарет" },
        { name: "Karmiel", nameHe: "כרמיאל", nameRu: "Кармиэль" }
      ]
    },
    {
      name: { en: "Center/Gush Dan", he: "מרכז/גוש דן", ru: "Центр/Гуш Дан" },
      cities: [
        { name: "Tel Aviv", nameHe: "תל אביב", nameRu: "Тель-Авив" },
        { name: "Ramat Gan", nameHe: "רמת גן", nameRu: "Рамат Ган" },
        { name: "Petah Tikva", nameHe: "פתח תקווה", nameRu: "Петах Тиква" },
        { name: "Bnei Brak", nameHe: "בני ברק", nameRu: "Бней Брак" },
        { name: "Herzliya", nameHe: "הרצליה", nameRu: "Герцлия" },
        { name: "Netanya", nameHe: "נתניה", nameRu: "Нетания" },
        { name: "Holon", nameHe: "חולון", nameRu: "Холон" },
        { name: "Bat Yam", nameHe: "בת ים", nameRu: "Бат-Ям" },
        { name: "Rishon LeZion", nameHe: "ראשון לציון", nameRu: "Ришон ле-Цион" },
        { name: "Rehovot", nameHe: "רחובות", nameRu: "Реховот" }
      ]
    },
    {
      name: { en: "Jerusalem & Samaria", he: "ירושלים ושומרון", ru: "Иерусалим и Самария" },
      cities: [
        { name: "Jerusalem", nameHe: "ירושלים", nameRu: "Иерусалим" },
        { name: "Bet Shemesh", nameHe: "בית שמש", nameRu: "Бейт Шемеш" },
        { name: "Modiin", nameHe: "מודיעין", nameRu: "Модиин" },
        { name: "Ariel", nameHe: "אריאל", nameRu: "Ариэль" },
        { name: "Maale Adumim", nameHe: "מעלה אדומים", nameRu: "Маале Адумим" }
      ]
    },
    {
      name: { en: "South", he: "דרום", ru: "Юг" },
      cities: [
        { name: "Beer Sheva", nameHe: "באר שבע", nameRu: "Беэр-Шева" },
        { name: "Ashkelon", nameHe: "אשקלון", nameRu: "Ашкелон" },
        { name: "Ashdod", nameHe: "אשדוד", nameRu: "Ашдод" },
        { name: "Eilat", nameHe: "אילת", nameRu: "Эйлат" },
        { name: "Dimona", nameHe: "דימונה", nameRu: "Димона" }
      ]
    }
  ];

  const getCityName = (city) => {
    if (currentLang === 'he') return city.nameHe;
    if (currentLang === 'ru') return city.nameRu;
    return city.name;
  };

  const getRegionName = (region) => {
    return region.name[currentLang];
  };

  return (
    <section id="areas" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {translations[currentLang].serviceAreasDetailed.title}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            {translations[currentLang].serviceAreasDetailed.subtitle}
          </p>
          
          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-blue-50 rounded-2xl p-6">
              <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse mb-2">
                <Clock className="w-6 h-6 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">24/7</span>
              </div>
              <p className="text-gray-700">{translations[currentLang].serviceAreasDetailed.stats.emergency}</p>
            </div>
            
            <div className="bg-green-50 rounded-2xl p-6">
              <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse mb-2">
                <MapPin className="w-6 h-6 text-green-600" />
                <span className="text-2xl font-bold text-green-600">50+</span>
              </div>
              <p className="text-gray-700">{translations[currentLang].serviceAreasDetailed.stats.cities}</p>
            </div>
            
            <div className="bg-yellow-50 rounded-2xl p-6">
              <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse mb-2">
                <Star className="w-6 h-6 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-600">5.0</span>
              </div>
              <p className="text-gray-700">{translations[currentLang].serviceAreasDetailed.stats.rating}</p>
            </div>
          </div>
        </div>

        {/* Introduction Text */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {translations[currentLang].serviceAreasDetailed.intro.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              {translations[currentLang].serviceAreasDetailed.intro.text1}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              {translations[currentLang].serviceAreasDetailed.intro.text2}
            </p>
          </div>
        </div>

        {/* Regions and Cities */}
        {regions.map((region, regionIndex) => (
          <div key={regionIndex} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {getRegionName(region)}
            </h2>
            
            <div className="space-y-8">
              {region.cities.map((city, cityIndex) => (
                <Card key={cityIndex} className="overflow-hidden shadow-lg">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-blue-900 mb-6">
                      {translations[currentLang].serviceAreasDetailed.cityTemplate.title
                        .replace('[city]', getCityName(city))}
                    </h3>
                    
                    <div className="prose max-w-none">
                      <p className="text-gray-700 leading-relaxed mb-4">
                        {translations[currentLang].serviceAreasDetailed.cityTemplate.paragraph1
                          .replace('[city]', getCityName(city))}
                      </p>
                      
                      <p className="text-gray-700 leading-relaxed mb-4">
                        {translations[currentLang].serviceAreasDetailed.cityTemplate.paragraph2
                          .replace('[city]', getCityName(city))}
                      </p>
                      
                      <p className="text-gray-700 leading-relaxed">
                        {translations[currentLang].serviceAreasDetailed.cityTemplate.paragraph3
                          .replace('[city]', getCityName(city))}
                      </p>
                    </div>

                    {/* Call to Action for each city */}
                    <div className="mt-6 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4">
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700 text-white flex items-center space-x-2 rtl:space-x-reverse"
                        onClick={() => window.open('tel:+972553002025')}
                      >
                        <Phone className="w-4 h-4" />
                        <span>+972-55-300-2025</span>
                      </Button>
                      
                      <Button 
                        variant="outline"
                        className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white flex items-center space-x-2 rtl:space-x-reverse"
                        onClick={() => window.open('https://wa.me/972553002025')}
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>WhatsApp</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-8 md:p-12 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">
              {translations[currentLang].serviceAreasDetailed.finalCta.title}
            </h3>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              {translations[currentLang].serviceAreasDetailed.finalCta.description}
            </p>
            
            <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
              ))}
              <span className="text-lg font-semibold text-gray-900 ml-2 rtl:mr-2">
                {translations[currentLang].serviceAreasDetailed.finalCta.googleRating}
              </span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg flex items-center space-x-3 rtl:space-x-reverse shadow-lg hover:shadow-xl transition-all"
                onClick={() => window.open('tel:+972553002025')}
              >
                <Phone className="w-5 h-5" />
                <span>+972-55-300-2025</span>
              </Button>
              
              <Button 
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg flex items-center space-x-3 rtl:space-x-reverse shadow-lg hover:shadow-xl transition-all"
                onClick={() => window.open('https://wa.me/972553002025')}
              >
                <MessageCircle className="w-5 h-5" />
                <span>WhatsApp</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreasDetailed;